'use client';

import * as React from 'react';
import { EventBus } from '@/shared/utils';
import { SeriesByID as SeriesDict } from '@/shared/i18n';
import { SeriesByIdEventKeys, SeriesByIdEvents } from '../events';
import { useSeriesById } from '../hooks/use-series-by-id';
import { SummaryDetails } from '../ui/components/summary-details';
import { VideoLessonDialog } from '../ui/components/dialogs/video-lesson';
import { LessonCompletionDialog } from '../ui/components/dialogs/lesson-completion';
import SeriesList from '../ui/components/series-list';
import { Auth } from 'firebase/auth';
import NavigationButton from 'src/components/layout/navigation-button';

type SeriesByIdScreenProps = {
  auth: Auth | null;
  eventBus: EventBus<SeriesByIdEvents>;
  dict: SeriesDict;
  seriesDocumentId: string;
  accessToken?: string;
  serieName: string;
};

export default function SeriesByIdScreen({
  auth,
  eventBus,
  dict,
  seriesDocumentId,
  accessToken,
  serieName,
}: SeriesByIdScreenProps) {
  const {
    fetchSeriesByIdSummary,
    fetchCustomerSubscription,
    createLessonProgresses,
    customerSubscription,
    fetchSeriesById,
    updateLessonProgresses,
    fetchFavourite,
    createFavourite,
    deleteFavourite,
  } = useSeriesById(auth, eventBus, accessToken);

  const isSubscriber = !!customerSubscription?.data?.subscriber;

  React.useEffect(() => {
    eventBus.on(SeriesByIdEventKeys.summary.view, fetchSeriesByIdSummary);
    eventBus.on(SeriesByIdEventKeys.customer.view, fetchCustomerSubscription);
    eventBus.on(SeriesByIdEventKeys.series.view, fetchSeriesById);
    eventBus.on(SeriesByIdEventKeys.lessonProgresses.update, updateLessonProgresses);
    eventBus.on(SeriesByIdEventKeys.lessonProgresses.create, createLessonProgresses);
    eventBus.on(SeriesByIdEventKeys.favourites.view, fetchFavourite);
    eventBus.on(SeriesByIdEventKeys.favourites.create, createFavourite);
    eventBus.on(SeriesByIdEventKeys.favourites.delete, deleteFavourite);

    return () => {
      eventBus.off(SeriesByIdEventKeys.summary.view, fetchSeriesByIdSummary);
      eventBus.off(SeriesByIdEventKeys.customer.view, fetchCustomerSubscription);
      eventBus.off(SeriesByIdEventKeys.series.view, fetchSeriesById);
      eventBus.off(SeriesByIdEventKeys.lessonProgresses.update, updateLessonProgresses);
      eventBus.off(SeriesByIdEventKeys.lessonProgresses.create, createLessonProgresses);
      eventBus.off(SeriesByIdEventKeys.favourites.view, fetchFavourite);
      eventBus.off(SeriesByIdEventKeys.favourites.create, createFavourite);
      eventBus.off(SeriesByIdEventKeys.favourites.delete, deleteFavourite);
    };
  }, [eventBus, seriesDocumentId]);

  return (
    <div>
      <NavigationButton
        label="Indietro"
        onClick={() => eventBus.emit(SeriesByIdEventKeys.redirects.back, undefined)}
      />
      <div>
        <SummaryDetails
          seriesDocumentId={seriesDocumentId}
          eventBus={eventBus}
          isSubscriber={isSubscriber}
        />
      </div>
      <div className="max-w-[800px] m-auto">
        <div className="my-12">
          <SeriesList
            seriesDocumentId={seriesDocumentId}
            serieName={serieName}
            eventBus={eventBus}
            isSubscriber={isSubscriber}
          />
        </div>
      </div>
      <VideoLessonDialog eventBus={eventBus} auth={auth} seriesDocumentId={seriesDocumentId} />
      <LessonCompletionDialog
        eventBus={eventBus}
        seriesDocumentId={seriesDocumentId}
        serieName={serieName}
      />
    </div>
  );
}
